/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import nodeAPI from '../../service/ApiServiceNode';
import { Table, ActionMenu, Button as DSButton } from '../../design-system';
import { Alert, Input, Select, message, Modal as AntModal, Button as AntButton, Space, Tag, Dropdown } from 'antd';
import { PlusOutlined, SearchOutlined, CloseCircleOutlined, UserOutlined, UserSwitchOutlined, StopOutlined, EyeOutlined, EditOutlined, FilterOutlined, SyncOutlined } from '@ant-design/icons';
import UserFormModal from './UserFormModal';
import { userService } from '../../services/userService';
import roleService from '../../service/RoleService';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { JOB_STATUS_LABELS } from '../../utils/enumNormalizer';
import AuthenticatedAvatar from '../Common/AuthenticatedAvatar';

const UserList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const sourceId = searchParams.get("prejoining");
    const [initialPrejoining, setInitialPrejoining] = useState(null);
    const canEditManagedUsers = roleService.hasAnyRole(['ROLE_HR', 'ROLE_FINANCE']) || roleService.isManager();
    const canDeactivateManagedUsers = roleService.isHR() || roleService.isManager();
    const canAddUsers = roleService.canManageHR();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [search, setSearch] = useState('');
    const [officeFilter, setOfficeFilter] = useState(null);
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [jobStatusFilter, setJobStatusFilter] = useState(null);
    const [sortConfig, setSortConfig] = useState({ field: null, order: null });
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [dropdowns, setDropdowns] = useState({ offices: [], departments: [], managers: [], roles: [] });
    const [replacementModalVisible, setReplacementModalVisible] = useState(false);
    const [pendingDeactivationUser, setPendingDeactivationUser] = useState(null);
    const [replacementManagerId, setReplacementManagerId] = useState(null);
    const [deactivatingWithReplacement, setDeactivatingWithReplacement] = useState(false);
    const [managerAccessDriftUsers, setManagerAccessDriftUsers] = useState([]);
    const [managerAccessDriftLoading, setManagerAccessDriftLoading] = useState(false);
    const [syncingManagerId, setSyncingManagerId] = useState(null);
    const [missingProbationExpiryCount, setMissingProbationExpiryCount] = useState(0);
    const [missingProbationExpiryOnly, setMissingProbationExpiryOnly] = useState(false);

    useEffect(() => {
        if (!sourceId || !canAddUsers) return;
        let active = true;
        nodeAPI.get(`/recr/prejoining/${sourceId}`).then(response => {
            if (!active) return;
            const source = response.data.data;
            if (source.status !== 'SUBMITTED' || source.employee_id) {
                message.error('This form must be submitted and reviewed before creating an employee.');
                return;
            }
            setInitialPrejoining(source); setEditingUser(null); setModalVisible(true);
        }).catch(() => { if (active) message.error('Unable to open the submitted pre-joining form.'); });
        return () => { active = false; };
    }, [sourceId, canAddUsers]);

    useEffect(() => {
        loadDropdowns();
        if (canAddUsers) {
            loadManagerAccessDrift();
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadUsers();
    }, [pagination.current, pagination.pageSize, search, officeFilter, departmentFilter, jobStatusFilter, missingProbationExpiryOnly, sortConfig.field, sortConfig.order]);

    const loadDropdowns = async () => {
        try {
            const data = await userService.getDropdowns();
            setDropdowns(data);
        } catch (error) {
            message.error('Failed to load dropdown data');
        }
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const result = await userService.getUsers({
                page: pagination.current,
                limit: pagination.pageSize,
                search,
                office_id: officeFilter,
                department_id: departmentFilter,
                job_status: jobStatusFilter,
                missing_probation_expiry: missingProbationExpiryOnly,
                sortBy: sortConfig.field,
                sortOrder: sortConfig.order === 'ascend' ? 'asc' : sortConfig.order === 'descend' ? 'desc' : null,
                is_active: true,
                include_joining: true
            });

            setUsers(result.users || []);
            setPagination(prev => ({ ...prev, total: result.pagination?.total || 0 }));
            setMissingProbationExpiryCount(result.missingProbationExpiryCount || 0);
        } catch (error) {
            message.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const loadManagerAccessDrift = async () => {
        if (!canAddUsers) return;

        setManagerAccessDriftLoading(true);
        try {
            const result = await userService.getManagerAccessDrift({ limit: 10 });
            setManagerAccessDriftUsers(result.users || []);
        } catch (error) {
            message.error('Failed to load manager access alerts');
        } finally {
            setManagerAccessDriftLoading(false);
        }
    };

    const handleAdd = () => {
        setInitialPrejoining(null);
        setEditingUser(null);
        setModalVisible(true);
    };

    const handleEdit = (user) => {
        navigate(`/admin/hr/employees/edit/${user.id}`);
    };

    const handleImpersonate = (_user) => {
        // TODO: Implement user impersonation
        // Requires ROLE_CAN_SWITCH_USER permission
        // Legacy: window.location.href = `/index?_switch_user=${user.email}`;
        message.info('Impersonate feature coming soon');
    };

    const handleDeactivate = (user) => {
        AntModal.confirm({
            title: 'Deactivate User',
            content: `Are you sure you want to deactivate "${user.full_name}"?`,
            okText: 'Deactivate',
            okType: 'warning',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await userService.deactivateUser(user.id);
                    message.success('User deactivated successfully');
                    loadUsers();
                    loadManagerAccessDrift();
                } catch (error) {
                    const errorMessage = getApiErrorMessage(error, 'Failed to deactivate user');
                    if (errorMessage.includes('Replacement manager is required')) {
                        setPendingDeactivationUser(user);
                        setReplacementManagerId(null);
                        setReplacementModalVisible(true);
                        return;
                    }
                    message.error(errorMessage);
                }
            }
        });
    };

    const handleReplacementDeactivate = async () => {
        if (!pendingDeactivationUser || !replacementManagerId) {
            message.error('Please select a replacement manager');
            return;
        }

        setDeactivatingWithReplacement(true);
        try {
            await userService.deactivateUser(pendingDeactivationUser.id, {
                replace_manager_id: replacementManagerId,
            });
            message.success('User deactivated successfully');
            setReplacementModalVisible(false);
            setPendingDeactivationUser(null);
            setReplacementManagerId(null);
            loadUsers();
            loadManagerAccessDrift();
        } catch (error) {
            message.error(getApiErrorMessage(error, 'Failed to deactivate user'));
        } finally {
            setDeactivatingWithReplacement(false);
        }
    };

    const closeReplacementModal = () => {
        setReplacementModalVisible(false);
        setPendingDeactivationUser(null);
        setReplacementManagerId(null);
    };

    const handleModalClose = (refresh = false) => {
        setModalVisible(false);
        setInitialPrejoining(null);
        if (sourceId) { const next = new URLSearchParams(searchParams); next.delete('prejoining'); setSearchParams(next, { replace: true }); }
        if (refresh) {
            loadUsers();
            loadDropdowns(); // Refresh dropdowns for new managers
            loadManagerAccessDrift();
        }
    };

    const handleSyncManagerAccess = async (userId) => {
        setSyncingManagerId(userId);
        try {
            await userService.syncManagerStatus(userId);
            message.success('Manager access synced successfully');
            await Promise.all([loadUsers(), loadManagerAccessDrift()]);
        } catch (error) {
            message.error(getApiErrorMessage(error, 'Failed to sync manager access'));
        } finally {
            setSyncingManagerId(null);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
    };

    const handleOfficeChange = (value) => {
        setOfficeFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleDepartmentChange = (value) => {
        setDepartmentFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleJobStatusChange = (value) => {
        setJobStatusFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const resetFilters = () => {
        setSearch('');
        setOfficeFilter(null);
        setDepartmentFilter(null);
        setJobStatusFilter(null);
        setMissingProbationExpiryOnly(false);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleReviewMissingProbationExpiry = () => {
        setSearch('');
        setJobStatusFilter(null);
        setMissingProbationExpiryOnly(true);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const jobStatusOptions = Object.entries(JOB_STATUS_LABELS).map(([value, label]) => ({
        value,
        label
    }));

    const getColumnSortOrder = (field) => (sortConfig.field === field ? sortConfig.order : null);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'full_name',
            key: 'full_name',
            width: '23%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('full_name'),
            render: (text, record) => {
                const nameMenuItems = [
                    {
                        key: 'view-profile',
                        label: 'View Profile',
                        icon: <EyeOutlined />
                    },
                    ...(canEditManagedUsers ? [{
                        key: 'edit-user',
                        label: 'Edit User',
                        icon: <EditOutlined />
                    }] : []),
                    ...(canDeactivateManagedUsers && record.is_active ? [
                        { type: 'divider' },
                        {
                            key: 'deactivate',
                            label: 'Deactivate',
                            icon: <StopOutlined style={{ color: '#FF9800' }} />,
                            style: { color: '#FF9800' }
                        }
                    ] : [])
                ];

                const handleNameMenuClick = ({ key }) => {
                    if (key === 'view-profile') {
                        navigate(`/profile/${record.id}`);
                    } else if (key === 'edit-user') {
                        handleEdit(record);
                    } else if (key === 'deactivate') {
                        handleDeactivate(record);
                    }
                };

                return (
                    <Space size="small">
                        <AuthenticatedAvatar
                            src={record.profile_picture}
                            alt={record.full_name}
                            icon={<UserOutlined />}
                            size={32}
                            style={{ backgroundColor: '#f56a00' }}
                        />
                        <Dropdown
                            menu={{ items: nameMenuItems, onClick: handleNameMenuClick }}
                            trigger={['click']}
                        >
                            <span
                                className="clickable-name"
                                style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                                onMouseEnter={(e) => e.target.style.color = '#FED230'}
                                onMouseLeave={(e) => e.target.style.color = '#1B1B1B'}
                            >
                                {text}
                            </span>
                        </Dropdown>
                        {record.status === 'status.joining' && <Tag color="gold">{record.email ? 'Access pending' : 'Company email pending'}</Tag>}
                        {record.join_date && record.join_date.slice(0, 10) > new Date().toISOString().slice(0, 10) && <Tag color="blue">Joining soon</Tag>}
                    </Space>
                );
            }
        },
        {
            title: 'Manager',
            dataIndex: ['manager', 'full_name'],
            key: 'manager',
            width: '17%',
            render: (text) => text || '[No Manager]'
        },
        {
            title: 'Office',
            dataIndex: ['office', 'full_name'],
            key: 'office',
            width: '10%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('office'),
            render: (text) => text || 'All (owner)'
        },
        {
            title: 'Role',
            dataIndex: 'roles',
            key: 'roles',
            width: '11%',
            render: (roles, record) => {
                const displayRole = roleService.getPublicRoleName(roles);
                return (
                    <span>
                        {displayRole}
                        {record.all_office_access && (
                            <i className="fa fa-key ml-1" title="Can access all offices" style={{ color: '#999' }}></i>
                        )}
                    </span>
                );
            }
        },
        {
            title: 'Billable Rate Card',
            dataIndex: ['user_role', 'name'],
            key: 'user_role',
            width: '18%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('user_role'),
            render: (text) => text || '-'
        },
        {
            title: 'Status',
            dataIndex: 'job_status',
            key: 'job_status',
            width: '10%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('job_status'),
            render: (status) => {
                if (!status) return '-';
                // Backend now sends formatted values: "Permanent", "Probation", etc.
                const color = status === 'Permanent' ? 'green' : status === 'Probation' ? 'orange' : 'default';
                return <Tag color={color}>{status}</Tag>;
            }
        },
        {
            title: 'Probation End',
            dataIndex: 'probation_end_date',
            key: 'probation_end_date',
            width: '18%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('probation_end_date'),
            render: (date, record) => {
                // Backend sends clean enum status: "probation" (mapped to display as "Probation")
                if (!date || record.job_status !== 'Probation') return '-';
                const formattedDate = new Date(date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                });
                const isPastDue = new Date(date) < new Date();
                return <span style={isPastDue ? { color: 'red' } : {}}>{formattedDate}</span>;
            }
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 80,
            fixed: 'right',
            render: (_, record) => {
                // Build menu items based on current user's permissions
                const menuItems = [];

                // EDIT: ROLE_HR OR ROLE_FINANCE
                if (canEditManagedUsers) {
                    menuItems.push('edit');
                }

                // IMPERSONATE: ROLE_CAN_SWITCH_USER (Management/Owner only)
                if (roleService.hasRole('ROLE_CAN_SWITCH_USER')) {
                    menuItems.push({
                        key: 'impersonate',
                        label: 'Impersonate',
                        icon: <UserSwitchOutlined style={{ color: '#418FD7' }} />,
                        color: '#418FD7'
                    });
                }

                // DEACTIVATE: ROLE_HR
                if (canDeactivateManagedUsers && menuItems.length > 0) {
                    menuItems.push({ type: 'divider' });
                    menuItems.push({
                        key: 'deactivate',
                        label: 'Deactivate',
                        icon: <StopOutlined style={{ color: '#FF9800' }} />,
                        color: '#FF9800'
                    });
                }

                // If no actions available, show dash
                if (menuItems.length === 0) {
                    return '-';
                }

                return (
                    <ActionMenu
                        items={menuItems}
                        record={record}
                        onAction={(key, record) => {
                            if (key === 'edit') {
                                handleEdit(record);
                            } else if (key === 'impersonate') {
                                handleImpersonate(record);
                            } else if (key === 'deactivate') {
                                handleDeactivate(record);
                            }
                        }}
                    />
                );
            }
        }
    ];

    const handleTableChange = (nextPagination, _filters, sorter) => {
        setPagination(prev => ({
            ...prev,
            current: nextPagination.current,
            pageSize: nextPagination.pageSize,
            total: prev.total
        }));

        if (!Array.isArray(sorter)) {
            setSortConfig({
                field: sorter?.order ? (sorter?.columnKey || sorter?.field || null) : null,
                order: sorter?.order || null
            });
        }
    };

    const renderManagerAccessAlertDescription = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {managerAccessDriftUsers.slice(0, 5).map((user) => (
                <div
                    key={user.id}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
                >
                    <span>
                        <strong>{user.full_name}</strong>
                        {' has '}
                        {user.active_direct_reports_count}
                        {' active direct report'}
                        {user.active_direct_reports_count === 1 ? '' : 's'}
                        {' but does not have Manager Access.'}
                    </span>
                    <Space size="small">
                        <DSButton
                            variant="secondary"
                            size="small"
                            uppercase={false}
                            onClick={() => handleEdit(user)}
                        >
                            Review
                        </DSButton>
                        <DSButton
                            variant="primary"
                            size="small"
                            uppercase={false}
                            icon={<SyncOutlined />}
                            loading={syncingManagerId === user.id}
                            disabled={Boolean(syncingManagerId) && syncingManagerId !== user.id}
                            onClick={() => handleSyncManagerAccess(user.id)}
                        >
                            Sync
                        </DSButton>
                    </Space>
                </div>
            ))}
            {managerAccessDriftUsers.length > 5 && (
                <span>{managerAccessDriftUsers.length - 5} more employees need manager access sync.</span>
            )}
        </div>
    );

    const renderMissingProbationExpiryAlertDescription = () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>
                {missingProbationExpiryCount}
                {' active probation employee'}
                {missingProbationExpiryCount === 1 ? '' : 's'}
                {' '}
                {missingProbationExpiryCount === 1 ? 'has' : 'have'}
                {' no probation end date. Set it from Edit User before appraisal scheduling or repair.'}
            </span>
            <Space size="small">
                {missingProbationExpiryOnly && <Tag color="orange">Showing affected</Tag>}
                <DSButton
                    variant="secondary"
                    size="small"
                    uppercase={false}
                    onClick={handleReviewMissingProbationExpiry}
                    disabled={missingProbationExpiryOnly}
                >
                    Review affected
                </DSButton>
            </Space>
        </div>
    );

    return (
        <div>
            {/* Header with Filters and Add Button */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Space size="small">
                        <Select
                            className="yellow-dropdown"
                            placeholder="Filter by Office"
                            style={{ minWidth: 200 }}
                            value={officeFilter}
                            onChange={handleOfficeChange}
                            allowClear
                            options={[
                                { label: 'All Offices', value: null },
                                ...dropdowns.offices.map(office => ({
                                    label: office.full_name,
                                    value: office.id
                                }))
                            ]}
                        />

                        <Select
                            className="yellow-dropdown"
                            placeholder="Filter by Department"
                            style={{ minWidth: 200 }}
                            value={departmentFilter}
                            onChange={handleDepartmentChange}
                            allowClear
                            options={[
                                { label: 'All Departments', value: null },
                                ...dropdowns.departments.map(dept => ({
                                    label: dept.name,
                                    value: dept.id
                                }))
                            ]}
                        />

                        <Select
                            className="yellow-dropdown"
                            placeholder="All Statuses"
                            style={{ minWidth: 200 }}
                            value={jobStatusFilter}
                            onChange={handleJobStatusChange}
                            allowClear
                            options={[
                                { label: 'All Statuses', value: null },
                                ...jobStatusOptions
                            ]}
                        />

                        <FilterOutlined
                            style={{
                                color: search || officeFilter || departmentFilter || jobStatusFilter ? '#FED230' : '#999',
                                marginRight: 8,
                                cursor: 'pointer',
                                fontSize: 16
                            }}
                            onClick={resetFilters}
                            title="Reset filters"
                        />
                    </Space>

                    <Space size="small">
                        {canAddUsers && (
                            <AntButton
                                className="btn-white-add"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                            >
                                ADD USER
                            </AntButton>
                        )}

                        <Input
                            placeholder="Search by name or email"
                            style={{ minWidth: 280 }}
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                            suffix={
                                search ? (
                                    <CloseCircleOutlined
                                        style={{
                                            color: "rgba(0,0,0,.25)",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => handleSearch("")}
                                    />
                                ) : null
                            }
                        />
                    </Space>
                </div>
            </div>

            {canAddUsers && managerAccessDriftUsers.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message="Manager access needs sync"
                    description={renderManagerAccessAlertDescription()}
                    style={{ marginBottom: 16 }}
                    closable
                />
            )}

            {missingProbationExpiryCount > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message="Probation end date missing"
                    description={renderMissingProbationExpiryAlertDescription()}
                    style={{ marginBottom: 16 }}
                    closable
                />
            )}

            {/* Table */}
            <Table
                columns={columns}
                dataSource={users}
                loading={loading || managerAccessDriftLoading}
                rowKey="id"
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} users`,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
                scroll={{ x: 1400 }}
                variant="standard"
                bordered
            />

            {/* Form Modal */}
            {modalVisible && (
                <UserFormModal
                    visible={modalVisible}
                    user={editingUser}
                    initialPrejoining={initialPrejoining}
                    dropdowns={dropdowns}
                    onClose={handleModalClose}
                />
            )}

            <AntModal
                title="Transfer Direct Reports"
                open={replacementModalVisible}
                onOk={handleReplacementDeactivate}
                onCancel={closeReplacementModal}
                okText="Transfer and Deactivate"
                okButtonProps={{ disabled: !replacementManagerId, loading: deactivatingWithReplacement }}
                cancelButtonProps={{ disabled: deactivatingWithReplacement }}
            >
                <Select
                    placeholder="Select replacement manager"
                    value={replacementManagerId}
                    onChange={setReplacementManagerId}
                    showSearch
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                >
                    {dropdowns.managers
                        .filter(manager => manager.is_active && manager.id !== pendingDeactivationUser?.id)
                        .map(manager => (
                            <Select.Option key={manager.id} value={manager.id}>
                                {manager.full_name || manager.email}
                            </Select.Option>
                        ))}
                </Select>
            </AntModal>
        </div>
    );
};

export default UserList;
